import { ExistingProvider, Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { Constructable } from 'morphism/dist/types/types';
import { UserStore } from '@/app/platform/platform-core/user/store/user.store';

/** Een store die bij een gebruikers- of appwissel naar zijn beginstand terug moet. */
export interface ResettableStore {
  reset(): void;
}

export const AUTO_RESET_AFTER_LOGOUT = new InjectionToken<ResettableStore[]>('');

export class AutoResetAfterLogoutProvider implements ExistingProvider {
  constructor(public readonly useExisting: Constructable<ResettableStore>) {}

  readonly provide = AUTO_RESET_AFTER_LOGOUT;
  readonly multi = true;
}

@Injectable({
  providedIn: 'root'
})
export class ClearStoresAfterLogoutGuard  {
  constructor(
    private readonly userStore: UserStore,
    private readonly logger: NGXLogger,
    @Optional()
    @Inject(AUTO_RESET_AFTER_LOGOUT)
    private readonly stores?: ResettableStore[]
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.handleCanActivate(route, state);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.handleCanActivate(childRoute, state);
  }

  private handleCanActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const userHasChanged = this.userStore.getValue().userHasJustChanged;

    if (userHasChanged) {
      if (!this.stores) {
        this.logger.info(
          'No stores configured to reset! Use AutoResetAfterLogoutProvider to reset the stores after a user has changed'
        );
      }
      this.stores?.forEach(async s => {
        s.reset();
      });

      this.userStore.update({
        userHasJustChanged: false
      });
    }

    return true;
  }
}
